import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { isEmptyObject } from '@common/helpers';
import { BaggageDisplayData, ChangeBaggageData } from '@common/interfaces/mybooking/baggage-request.interface';
import {
  FirstBaggage,
  FirstBaggageBoundMap,
  FirstBaggagePassengerApplyInfo,
} from '@common/interfaces/mybooking/mybooking.interface';
import { FirstBaggagePassengerMap, FirstBaggagePnr } from '@common/interfaces/mybooking/order-baggage.interface';
import { ServicingCommonStoreService } from '@common/services';
import { BaggageRequestService } from '@common/services/my-booking/baggage-request/baggage-request.service';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { SupportComponent } from '@lib/components/support-class';
import { AmountFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { GetOrderResponseData, GetOrderResponseDataOrderType } from 'src/sdk-servicing';
import { OrderSeatItemSeatSelection } from 'src/sdk-servicing/model/orderSeatItemSeatSelection';
import { v4 as uuidV4 } from 'uuid';

@Component({
  selector: 'asw-baggage-cataloge',
  templateUrl: './baggage-cataloge.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaggageCatalogeComponent extends SupportComponent {
  pnr: GetOrderResponseData = {};
  @Input() airBoundId?: string = '';
  @Input() travelerId?: string = '';

  @Output() changeBaggage = new EventEmitter<ChangeBaggageData>();

  firstBaggagePassengerMap: FirstBaggagePassengerMap = {};

  baggageDisplay: BaggageDisplayData = {};

  isExistServiceStatus: boolean = false;

  serviceAmount: number = 0;

  _subscription: Subscription = new Subscription();
  firstBaggage!: FirstBaggage;

  uuid: string = uuidV4();

  constructor(
    private _common: CommonLibService,
    public _amountFormatPipe: AmountFormatPipe,
    private _myBookingServiceService: MyBookingServiceService,
    private _baggageRequestService: BaggageRequestService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _changeDetector: ChangeDetectorRef,
    private staticMsg: StaticMsgPipe
  ) {
    super(_common);
  }

  init() {
    this._subscription.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.pnr = servicingCommon.getOrder ?? ({} as GetOrderResponseData);
        this.firstBaggage = servicingCommon.firstBaggage ?? ({} as FirstBaggage);
        this.mappingInItBaggage();
        this._changeDetector.markForCheck();
      })
    );
  }

  mappingInItBaggage() {
    // 当該搭乗者手荷物情報と
    this.firstBaggagePassengerMap = this._baggageRequestService.getBaggageService(
      String(this.airBoundId),
      String(this.travelerId),
      this.pnr.services?.baggage?.firstBaggage as FirstBaggagePnr
    );

    // ＜ここから、当該搭乗者手荷物情報.catalogue[]の繰り返し(それぞれ要素が当該カタログ情報) ＞
    // (12.1) 1.当該搭乗者手荷物情報.serviceStatus=notRequested(未登録)場合、
    if (this.firstBaggagePassengerMap?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.NotRequested) {
      const boundInfoOfBaggageMapping = this.firstBaggage?.[String(this.airBoundId)] as FirstBaggageBoundMap;
      const baggageItem =
        (boundInfoOfBaggageMapping?.[String(this.travelerId)] as FirstBaggagePassengerApplyInfo) ?? {};
      // [12.1.1]
      if (isEmptyObject(baggageItem) !== true) {
        this.baggageDisplay = {
          paidServiceAmount: this?.firstBaggagePassengerMap?.catalogue?.prices?.total,
          currencyCode: this?.firstBaggagePassengerMap.catalogue?.prices?.currencyCode,
          isActive: true,
          isSelected: true,
        };
      }
      // [12.1.2]
      else {
        this.baggageDisplay = {
          paidServiceAmount: this?.firstBaggagePassengerMap.catalogue?.prices?.total,
          currencyCode: this?.firstBaggagePassengerMap.catalogue?.prices?.currencyCode,
          isActive: true,
          isSelected: false,
        };
      }
    }
    // [12.2] 当該搭乗者手荷物情報.serviceStatus≠notRequested(未登録)場合、
    if (this.firstBaggagePassengerMap?.serviceStatus !== OrderSeatItemSeatSelection.ServiceStatusEnum.NotRequested) {
      if (
        // [12.2.1]
        this.firstBaggagePassengerMap?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Confirmed ||
        (this.firstBaggagePassengerMap?.serviceStatus ===
          OrderSeatItemSeatSelection.ServiceStatusEnum.ConfirmedWithoutTsm &&
          this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh)
      ) {
        this.baggageDisplay = {
          paidServiceAmount: this.firstBaggagePassengerMap?.prices?.total,
          currencyCode: this.firstBaggagePassengerMap?.prices?.currencyCode,
          isActive: false,
          isSelected: true,
        };
      }
      // [12.2.2]
      else {
        const label = this.getApplicationStatus();
        this.baggageDisplay = {
          isDisplayStatus: true,
          status: label,
        };
      }
    }
  }

  get isShowAmount() {
    return this.firstBaggagePassengerMap?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.NotRequested;
  }

  // [12] サービス金額
  get getServiceAmount() {
    return this._amountFormatPipe.transform(
      this.firstBaggagePassengerMap?.catalogue?.prices?.total ?? 0,
      undefined,
      this.firstBaggagePassengerMap?.catalogue?.prices?.currencyCode ?? ''
    );
  }

  // [13] 申込状態
  getApplicationStatus() {
    switch (this.firstBaggagePassengerMap?.serviceStatus) {
      case OrderSeatItemSeatSelection.ServiceStatusEnum.Prebooked:
        return this.staticMsg.transform('label.waitingForEmd');
      case OrderSeatItemSeatSelection.ServiceStatusEnum.ConfirmedWithoutTsm:
        if (this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds) {
          return this.staticMsg.transform('label.appliedByOtherCarrier');
        } else {
          return '';
        }
      case OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled:
        return this.staticMsg.transform('label.notApplied');
      case OrderSeatItemSeatSelection.ServiceStatusEnum.Requesting:
        return this.staticMsg.transform('label.applying');
      case OrderSeatItemSeatSelection.ServiceStatusEnum.HasDiscrepancy:
        return this.staticMsg.transform('label.appliedInPart');
      default:
        return '';
    }
  }

  // (4) 手荷物選択処理
  onCheckboxChange(e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    let paramsUpdate = {
      isCheck: checked,
      airBoundId: String(this.airBoundId),
      travelerId: String(this.travelerId),
    };
    this.baggageDisplay = {
      ...this.baggageDisplay,
      isSelected: checked,
    };
    if (checked) {
      paramsUpdate = {
        ...paramsUpdate,
        ...{ paidServiceAmount: this.firstBaggagePassengerMap?.catalogue?.prices?.total ?? 0 },
      };
      this.changeBaggage.emit(paramsUpdate);
    } else {
      this.changeBaggage.emit(paramsUpdate);
    }
  }

  reload(): void {}

  destroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
